/**
 * GlobalChatSystem Types
 * 
 * This file defines all TypeScript interfaces and types for the global chat system.
 * These types are used across all pages that implement the chat functionality.
 */

import { ACCEPTED_FILE_TYPES } from '../../constants/attachableFileTypes';
import { ConstructionWaitlistController } from '../../hooks/useConstructionWaitlist';
import { MAX_UPLOAD_BYTES_PER_FILE } from '../../constants/uploads';

// ============================================================================
// Core Chat Types
// ============================================================================

/**
 * Workflow output metadata for downstream workflow chaining.
 * Embedded in chat messages to enable re-hydration of content
 * when buttons are clicked or page is refreshed.
 */
export interface WorkflowOutputMetadata {
  /** Type of workflow output */
  type: 'research_report' | 'formatted_content' | 'video_script' | 'translation';
  /** The content that downstream workflows can use */
  content: string;
  /** Title/label for the content */
  title?: string;
  /** Timestamp when created */
  timestamp: Date;
  /** Source workflow ID */
  sourceWorkflow: string;
}

/**
 * A user message that has been accepted into a thread's history
 * (its bubble is rendered) but has NOT yet been dispatched to the
 * orchestrator. Lives in `state.pendingByThread[threadId]` until the
 * in-flight turn for that thread finishes; then it is dequeued and
 * fired as a fresh turn.
 *
 * Mirrors / re-exports the shape defined in `threadQueue.ts`. Lives
 * here so `GlobalChatState` and the GlobalChatAction union can refer
 * to it without a circular import (threadQueue.ts imports `ChatMessage`
 * from this file).
 *
 * See PR feat/same-thread-queue-while-streaming.
 */
export interface PendingMessage {
  id: string;
  text: string;
  html?: string;
  attachmentsSerialized?: SerializedAttachment[];
  /**
   * Ingest-on-upload (2026-06 reliability overhaul): upload-time extraction
   * job id captured at enqueue time so a queued turn still carries it when
   * the drain effect fires it later. See ChatMessage.ingestJobId.
   */
  ingestJobId?: string;
  /**
   * Delivery ladder (PR-2): the task id minted at send time, so a queued or
   * offline-held turn dispatches under the SAME id the bubble carries and
   * the server converges on one row instead of minting a second.
   */
  clientTaskId?: string;
  /** The bubble's shared-store id (see ChatMessage.sharedMessageId). */
  sharedMessageId?: string;
  enqueuedAt: number;
  threadId: string;
  status: 'queued' | 'aborted';
  abortedNote?: string;
}

/**
 * Represents a single chat message in the conversation.
 */
export interface ChatMessage {
  /** Unique identifier for reliable updates (immune to React dispatch race conditions) */
  id?: string;
  /** The plain text content of the message */
  text: string;
  /** Whether this message was sent by the user (true) or the AI/system (false) */
  isUser: boolean;
  /** Structured event payload retained on data-only posts (renderPolicy
   *  'never', 2026-08-20) — the standing-connect-offer dedupe and future
   *  turn context read it back. Absent on renderer-composed messages. */
  payload?: import('../../services/globalTools/structuredEvents').ToolStructuredEventBase;
  /**
   * Greeting messages only (id/sourceTag 'welcome') — monotonic quality rank
   * of the copy this greeting was composed from (2026-08-09 multi-tab
   * greeting parity; see services/threadMetadata/greetingRank). The
   * stale-greeting reconciliation replaces a persisted greeting only on a
   * STRICTLY higher rank, so every tab converges on the best copy any tab
   * ever computed. Absent on non-greeting messages and pre-ranking history.
   */
  greetingRank?: number;
  /**
   * Greeting messages only — the one-click starter prompts that belong WITH
   * this greeting. Persisted so every tab/session shows the same chips for
   * the same thread, independent of each tab's own config-load timing.
   */
  starters?: string[];
  /**
   * Local system/UI notice rather than a conversational turn (W3b summon-gate
   * hints, 2026-08-07). Writers were already stamping this through `as` casts;
   * declared here so readers (e.g. the mention-only guard's duplicate check)
   * can consume it without an intersection cast. Absent ⇒ ordinary message.
   */
  isSystem?: boolean;
  /**
   * Group-threading (P3): the authenticated uid of the human who sent this
   * message (or the 'assistant' sentinel for AI turns). Optional + absent on solo
   * threads and on every pre-P3 message — rendering falls back to the isUser role
   * split when it's missing. It is first-class + REQUIRED only on the shared
   * group-thread store (see src/types/groupThreadMessageDoc.ts); here it's an
   * additive, inert field so it can be surfaced without touching solo behavior.
   */
  senderId?: string;
  /**
   * WHO COMPOSED THE WORDS (T1, 2026-09-10 — Miles's live avatar test: "he was
   * messaging on behalf of me and shouldn't under any circumstance").
   *
   * `isUser` says whose NAME AND AVATAR a message wears; `senderId` says which
   * person, in a room with several; this says who WROTE it. They disagree in
   * exactly one case — a post Prest0n composed at someone's instruction — and
   * that disagreement is what the permanent marker in MessageSenderChip shows.
   * Absent on every message written before today, and the render falls back to
   * the `isUser` split, so existing threads look exactly as they did.
   * See src/types/messageAuthorship.ts.
   */
  authorship?: import('../../types/messageAuthorship').MessageAuthorship;
  /**
   * The sender's display name as the SERVER resolved it at write time
   * (2026-08-26 sender-name work). Attribution used to depend entirely on the
   * viewer's client-side roster state, which rendered "Teammate kFZ5" for a
   * real teammate whenever the roster was late, failed, or belonged to a
   * different workspace than the sender. The name travels with the message as
   * the floor; a fresher client-side roster label still wins at render.
   */
  senderDisplayName?: string;
  /**
   * Shared-store sort key, carried through from the group-thread doc.
   *
   * INCIDENT 2026-08-06: the server writes a turn's question and its answer with
   * the SAME `orderTs`, ordering them by `seq` (question 0, answer 1). That order
   * was applied once when the overlay docs were deserialized and then THROWN
   * AWAY, because neither field survived onto ChatMessage — so the final merge,
   * which sorts by `timestamp` alone, could render Prest0n's reply above the
   * message it was answering. It did, on other participants' screens, which read
   * as Prest0n talking twice.
   *
   * Optional and absent for solo threads and every pre-P3 message; the sort
   * falls back to `timestamp` exactly as before when they are missing.
   */
  orderTs?: number;
  /** Position within one turn: question = 0, answer = 1. See `orderTs`. */
  seq?: number;
  /** When the message was sent */
  timestamp: Date;
  /** Optional file attachments included with the message */
  attachments?: File[];
  /** Optional serialized attachments for persistence/restoration */
  attachmentsSerialized?: SerializedAttachment[];
  /** Optional rich HTML content for platform messages */
  html?: string;
  /** Optional source tag for system messages */
  sourceTag?: string;
  /** Optional token usage metadata from AI model responses (single model) */
  tokenUsage?: TokenUsageMetadata;
  /** Persisted snapshot of token usage/cost for durability across sessions (single model) */
  tokenUsageSnapshot?: TokenUsageSnapshot;
  /** Optional array of token usages for multi-model responses (e.g., Stage 2 + Stage 3 deep research) */
  tokenUsages?: TokenUsageMetadata[];
  /** Persisted snapshots for multi-model responses */
  tokenUsageSnapshots?: TokenUsageSnapshot[];
  /** Optional action buttons for special message tiles */
  actionButtons?: SpecialMessageActionButton[];
  /** Optional message type for special tile styling */
  messageType?: SpecialMessageType;
  /** Optional priority level for special tile prominence */
  priority?: SpecialMessagePriority;
  /** Optional title for special message tiles */
  title?: string;
  /** Workflow output metadata for downstream workflow chaining */
  workflowOutput?: WorkflowOutputMetadata;
  /** HTML for workflow action buttons - rendered after attachments */
  workflowActionButtonsHtml?: string;
  /** Index of selected action button (for persisting user's button choice) */
  selectedActionButtonIndex?: number;
  /** If true, message is included in context for AI but hidden from UI (e.g., document analysis) */
  isContextOnly?: boolean;
  /**
   * Diagnostic breadcrumb stamped by the whole-array thread doc's card-html
   * backstop (chatThreadMessagesFirestore.ts): the message's card html was
   * blanked to fit the Firestore byte budget. The payload survives, and the
   * connect-tile rebuild (connectTileRehydrate.ts) re-projects the card from
   * it at the SET_MESSAGES boundary — the flag stays for diagnostics.
   */
  cardHtmlDroppedForFirestore?: boolean;
  /** Set when the card html was re-projected from `payload` (see above). */
  cardHtmlRebuilt?: boolean;
  /** Tags for categorization (e.g., "test example" for test data) */
  tags?: string[];
  /**
   * Lifecycle status of an assistant message. Point 6 (mid-stream hard-refresh
   * data-loss fix): a turn runs entirely client-side, so a hard refresh while a
   * deep-research turn is streaming leaves a dead "thinking" widget that can never
   * finalize. On reload we detect such an orphaned in-flight widget and mark it
   * `'interrupted'` so the renderer shows a recoverable notice instead of a
   * never-completing spinner. `'streaming'` is informational; `'complete'` is the
   * default for finalized messages (absence === complete for back-compat).
   */
  status?: 'streaming' | 'complete' | 'interrupted';
  /**
   * Cheap boolean the renderer can branch on without re-deriving `status`.
   * True only when this assistant message was an in-flight turn interrupted by a
   * page refresh (the run is gone and cannot resume itself).
   */
  interrupted?: boolean;
  /**
   * The original user prompt captured for one-click "Regenerate" on an interrupted
   * turn. Preserved from the immediately-preceding user bubble at the moment we mark
   * the assistant message interrupted, so a single tap can re-run the lost turn.
   */
  regeneratePrompt?: string;
  /**
   * Point 6 (Option B) — server-side durable guest deep-research task id.
   *
   * When a GUEST (unauthenticated) deep-research run is executed SERVER-SIDE
   * (via the guestStartDeepResearch Cloud Function), the unguessable task id is
   * stamped onto the in-flight assistant message and PERSISTED with it
   * (localStorage thread store / Firestore). On a hard refresh, the hydrate
   * path (useGlobalChat) sees an in-flight message carrying `serverTaskId` and
   * RE-ATTACHES by re-polling guestPollTask(serverTaskId) instead of marking it
   * interrupted — this is the refresh-survival behavior. Absent on the
   * migrated/paid-account path (that path is unchanged and uses prest0nRunTurn).
   */
  serverTaskId?: string;
  /**
   * Phase 3 per-message-doc CONVERGENCE ANCHOR (read-cutover). A durable,
   * write-once id that ties one logical server-backed answer to a SINGLE
   * per-message Firestore doc across all three writers (the live client bubble,
   * the client hydrate-recovery write, and the server task-completion write).
   *
   * Derived deterministically as `prest0n_task_<serverTaskId>` the first time a
   * message acquires a `serverTaskId`, and — unlike `serverTaskId`, which is
   * cleared on finalize so resume won't re-attach — it is NEVER cleared. It is
   * used as the per-message-doc key AND the dual-read merge key, so the client's
   * `crypto.randomUUID` bubble id and the server's `prest0n_task_<taskId>` id
   * collapse to one document instead of duplicating the report. Absent on
   * messages that never had a server task (plain chat) — those key by their own
   * id, exactly as before.
   */
  taskAnchorId?: string;
  /**
   * Delivery ladder (2026-09-08, chat-thread experience PR-2): the id this
   * message carries in the SHARED store — `prest0n_task_<clientTaskId>__user`
   * for a turn (the task id is minted BEFORE the bubble exists, so the bubble
   * already knows the doc the server will write), or the post's own id for a
   * summon-suppressed message. Persisted: it is the join key between the
   * sender's bubble, the group-store echo and the server doc. Absent on
   * assistant messages and on everything sent before this landed.
   */
  sharedMessageId?: string;
  /** Render-only, never persisted (clientOnlyMessageFields): the ladder rung shown on the bubble. */
  deliveryStatus?: 'pending' | 'queued-offline' | 'sent' | 'delivered' | 'read' | 'failed';
  /** Render-only: why a send failed, for the retry tooltip. */
  deliveryFailure?: string;
  /**
   * Surface-plan P2 (2026-08-29): stamp on an ADDITIONAL surface message
   * delivered in the same turn's series as a parent card/answer — carries the
   * PARENT's anchor id (`prest0n_task_<taskId>`), while the sibling doc
   * itself keys as `<anchor>__s<order>` (surfaceSeriesDocId). The turn-aware
   * CTA liveness rule (workflowCtaLifecycle, flag-gated) skips
   * sibling-stamped messages when deciding whether a card's buttons retire —
   * a file or receipt delivered in the SAME turn never kills its own turn's
   * card. Absent on every message today; emitters arrive with P5/P7.
   */
  surfaceSeriesAnchor?: string;
  /**
   * ONE ATTRIBUTION PER TURN (owner directive 2026-09-01, the Connect-Fitbit
   * repro): a card tile posted by a turn carries the turn's token-usage series
   * as an INHERITED COPY (useGlobalTools stampTurnUsageOntoCards, 2026-08-29)
   * so its ⓘ popup is truthful when the card is the turn's only surface. It
   * is not a second model call. This flag marks that copy so the transcript
   * can render the ⓘ once per turn — on the answer bubble that owns the
   * series — and suppress it on the card when that bubble is present
   * (turnAttribution.ts). Absent on cards whose usage is their own (a
   * structured-event render) and on every non-card message.
   */
  tokenUsageInherited?: boolean;
  /**
   * Ingest-on-upload (2026-06 reliability overhaul) — upload-time extraction
   * job id for a USER message with attachments. handleSend stamps it after the
   * GCS signed-URL uploads succeed and `prest0nStartIngest` returns a jobId, so
   * the turn dispatch (useGlobalTools → sendTurn) can (a) include `ingestJobId`
   * in the prest0nRunTurn body (backend reuses the upload-time extraction) and
   * (b) live-subscribe to `accounts/{accountId}/ingestJobs/{jobId}` for the
   * "Processing documents: n of m…" pending label. Absent when cloud extract is
   * off, uploads failed, or the ingest kickoff failed (backend extracts inline).
   */
  ingestJobId?: string;
  /**
   * ROUTINE RUN STAMP (Routine Threads, 2026-07-31): present on assistant
   * messages produced by a routine's scheduled execution. Stamped server-side
   * (writeAnchoredMessageToThread) from the task payload; the client renders
   * a small banner chip above the bubble (glyph + "Routine · {name} ·
   * {relative time}"). Plain JSON — round-trips through every persistence
   * boundary (threadPersist strips only `attachments`; the message doc
   * mappers pass unknown fields through). Absent on all other messages.
   */
  routineRun?: {
    /** The routine's id (== its thread id). */
    routineId: string;
    /** Routine name at run time (banner label). */
    name: string;
    /** ISO of the run's scheduled fire time. */
    scheduledForIso: string;
  };
}

/**
 * Payload for programmatically adding a system/AI message.
 *
 * 2026-05-10 (Miles directive, Slack C0AMX0ACS9K/1778451390.755569):
 *   `payload?: ToolStructuredEventBase` is the canonical field for new
 *   tool code. The post-tool renderer composes the user-visible reply
 *   from this. The legacy `text` field remains accepted at the type
 *   level for back-compat during the migration; the CI lint
 *   `scripts/forbid-hardcoded-user-strings.py` is the active enforcement
 *   gate for new code (see Phase D / Phase E commits).
 */
export interface SystemMessageInput {
  /** Optional unique identifier (will be auto-generated if not provided) */
  id?: string;
  /**
   * Structured event payload — the post-tool renderer composes the
   * user-visible reply from this. New tool code MUST emit this field
   * instead of `text`. See
   * src/services/globalTools/structuredEvents.ts for the event-shape
   * discriminated union.
   */
  payload?: import('../../services/globalTools/structuredEvents').ToolStructuredEventBase;
  /**
   * 'never' = post the payload DATA-ONLY: no renderer model call, no
   * composed prose (2026-08-20 orchestrator-authorship directive — the
   * connect cards are self-contained and the orchestrator's own streamed
   * sentence accompanies them; a client side-model must not also compose).
   * The payload + buttons persist on the message for dedupe/context.
   */
  renderPolicy?: 'never';
  /**
   * Plain-text content of the message.
   * @deprecated **DO NOT USE in new tool code.** Tools must emit `payload`
   *   instead and let the post-tool renderer compose the text. Accepted
   *   for back-compat during the migration; CI lint
   *   `scripts/forbid-hardcoded-user-strings.py` flags new literals.
   */
  text?: string;
  html?: string;
  attachments?: File[];
  attachmentsSerialized?: SerializedAttachment[];
  sourceTag?: string;
  timestamp?: Date;
  /** Optional token usage metadata from AI model responses (single model) */
  tokenUsage?: TokenUsageMetadata;
  /** Optional persisted token usage snapshot (single model) */
  tokenUsageSnapshot?: TokenUsageSnapshot;
  /** Optional array of token usages for multi-model responses */
  tokenUsages?: TokenUsageMetadata[];
  /** Persisted snapshots for multi-model responses */
  tokenUsageSnapshots?: TokenUsageSnapshot[];
  /** Optional action buttons for special message tiles */
  actionButtons?: SpecialMessageActionButton[];
  /** Optional message type for special tile styling */
  messageType?: SpecialMessageType;
  /** Optional priority level for special tile prominence */
  priority?: SpecialMessagePriority;
  /** Optional title for special message tiles */
  title?: string;
  /** Workflow output metadata for downstream workflow chaining */
  workflowOutput?: WorkflowOutputMetadata;
  /** HTML for workflow action buttons - rendered after attachments */
  workflowActionButtonsHtml?: string;
  /** Index of selected action button (for persisting user's button choice) */
  selectedActionButtonIndex?: number;
  /** Tags for categorization (e.g., "test example" for test data) */
  tags?: string[];
}

/**
 * Streaming message helpers for real-time message updates.
 */
export interface StreamingMessageHelpers {
  /** Start a new streaming message and return its index and unique ID for updates */
  startStreamingMessage: (initial: SystemMessageInput) => { index: number; id: string };
  /** Update a streaming message by index with new content (legacy - may have race conditions in chains) */
  updateStreamingMessage: (index: number, update: Partial<ChatMessage>) => void;
  /** Update a streaming message by ID (race-condition-safe) */
  updateStreamingMessageById?: (id: string, update: Partial<ChatMessage>) => void;
  /** Finalize a streaming message by index (legacy - may have race conditions in chains) */
  finalizeStreamingMessage: (index: number, final: Partial<ChatMessage>) => void;
  /** Finalize a streaming message by ID (race-condition-safe) */
  finalizeStreamingMessageById?: (id: string, final: Partial<ChatMessage>) => void;
  /** Remove a message by index (e.g., remove thinking indicator) */
  removeMessage: (index: number) => void;
  /** Remove a message by ID (race-condition-safe) */
  removeMessageById?: (id: string) => void;
}

/**
 * Handler invoked when the user sends a message.
 * Return true to mark the message as fully handled (skip defaults).
 */
export type OnUserMessageHandler = (
  message: ChatMessage,
  helpers: {
    addSystemMessage: (input: SystemMessageInput) => ChatMessage;
    addMessage: (message: ChatMessage) => ChatMessage;
    /** Streaming helpers for real-time message updates */
    streaming: StreamingMessageHelpers;
  }
) => Promise<boolean | void> | boolean | void;

/**
 * Optional canned auto-reply configuration for per-page chat behavior.
 * Useful for pages where the custom bot is not yet available.
 */
export interface AutoReplyConfig {
  /** Plain text to send back immediately after each user message */
  text: string;
  /** Optional tag to identify the message origin (e.g., 'construction') */
  sourceTag?: string;
  /** Optional delay before sending the auto-reply (milliseconds) */
  delayMs?: number;
}

/**
 * Serialized representation of an attachment for persistence
 */
export interface SerializedAttachment {
  name: string;
  type: string;
  size: number;
  lastModified?: number;
  /** Data URL (base64) to restore attachment for display - used for localStorage persistence */
  dataUrl?: string;
  /** Firebase Storage download URL - used for cross-device/cross-session persistence */
  storageUrl?: string;
  /**
   * When `storageUrl` was minted (ms since epoch). A signed download URL lives 7 days;
   * the image tile re-mints proactively past SIGNED_URL_STALE_MS so a picture never
   * breaks on day 8 (chat-thread PR-6, 2026-09-09). Absent on records minted before.
   */
  storageUrlMintedAt?: number;
  /** Firebase Storage path for reference/deletion */
  storagePath?: string;
  /** GCS ingest path for cloud-native extraction */
  gcsPath?: string;
  /** The planner tool that produced a generated file (generate_video, generate_audio, …) —
   *  stamped server-side; lets a follow-up turn name "the pouring video" by what made it. */
  generatedBy?: string;
  /** DOC-COMPLETENESS (2026-09-01): generated-document identity — additive,
   *  optional forever (absent = today's card, byte-identical). Stamped
   *  server-side by the docRegistry delivery seam. */
  docId?: string;
  docVersion?: number;
  supersedes?: { docId: string; version: number };
  sourcePath?: string;
}

// ============================================================================
// Token Usage Types
// ============================================================================

/**
 * AI provider identifiers for token usage tracking.
 * Widened 2026-07-06 for the SSOT tier-ladder families (Titan/Pulse/Zenith/
 * Maxim/Fathom/Lunar/Mirage/Quill) — provider derivation now emits these, so
 * keeping the union narrow just forced `as any` laundering at every crossing.
 * Keep in lockstep with the copy in src/types/walletFirestore.ts.
 */
export type AIProvider =
  | 'perplexity'
  | 'claude'
  | 'openai'
  | 'gemini'
  | 'grok'
  | 'zai'
  | 'deepseek'
  | 'minimax'
  | 'moonshot'
  | 'xiaomi'
  | 'qwen'
  | 'openrouter'
  | 'unknown';

/**
 * Token usage metadata captured from AI model responses.
 * This data is displayed in the token info popup for each AI message.
 */
export interface TokenUsageMetadata {
  /** Number of tokens in the input/prompt */
  inputTokens?: number;
  /** Number of tokens in the output/completion */
  outputTokens?: number;
  /** Number of tokens used for reasoning/thinking (billed differently by some providers) */
  reasoningTokens?: number;
  /** Number of tokens used for citations (Perplexity) */
  citationTokens?: number;
  /** Number of search queries performed (Perplexity Deep Research) - billed per query, not per token */
  searchQueries?: number;
  /** Total tokens used (input + output) */
  totalTokens?: number;
  /** The AI model used (e.g., "sonar-deep-research", "claude-opus-4.7") */
  model?: string;
  /** The AI provider (perplexity, claude, openai, etc.) */
  provider?: AIProvider;
  /** Response time in milliseconds from request start to completion */
  responseTimeMs?: number;
  /** When the AI response was generated */
  timestamp?: Date;
  /** Brief description of what this AI usage was for (e.g., 'research strategy', 'deep research') */
  summary?: string;
  /** API finish reason: 'stop' (normal completion), 'length' (hit max_tokens cap), etc. */
  finishReason?: string;
  /** Upstream inference cost in USD as reported by OpenRouter (authoritative, includes search fees) */
  upstreamCostUsd?: number;
  /** Reasoning-effort level the call ran at (none…max) — renders as the
   *  dimmed "· effort" suffix after the public system name (2026-08-14). */
  effort?: string;
  /** 2026-08-16 (Miles): this step's cost is billed on its own lane (app-tool
   *  metering / fleet-proxy rows) — the ⓘ shows it, expense layers skip it. */
  billedSeparately?: boolean;
  /** Background-work stage (thread-ledger PR-5): 'ledger-compaction' |
   *  'memory-extraction' | 'media-render' | 'read-aloud' — the ⓘ prefers this over the sidecarKey prefix. */
  stage?: string;
  /** Deterministic key of a billedSeparately sidecar step (`memext_…` memory
   *  update, `threadfup_…` follow-up suggestions) — server-stamped for dedupe;
   *  the ⓘ derives the step's background-lane label from its prefix. */
  sidecarKey?: string;
  /** 2026-09-08 (media usage tracked always): 'estimate' when the platform priced a
   *  non-token media render (picture / video / audio) from its measured units and the
   *  SSOT unit price — the ⓘ marks it "≈"; 'provider' when the vendor reported the cost. */
  costSource?: 'provider' | 'estimate';
}

/**
 * Cost snapshot for persisted token usage (numbers only for minimal storage).
 */
export interface TokenCostSnapshot {
  inputCost?: number;
  outputCost?: number;
  reasoningCost?: number;
  citationCost?: number;
  searchCost?: number;
  totalCost?: number;
  model?: string;
  provider?: string;
}

/**
 * Minimal, serializable snapshot of token usage and costs for persistence.
 */
export interface TokenUsageSnapshot extends TokenUsageMetadata {
  /** ISO timestamp string for the response (persist-friendly) */
  timestampIso?: string;
  /** Precomputed cost breakdown at time of persistence */
  cost?: TokenCostSnapshot;
}

/**
 * Configuration options for the chat system behavior.
 * Different pages can customize how the chat behaves.
 */
export interface GlobalChatConfig {
  /** Unique identifier for this chat instance (used for localStorage keys) */
  instanceId: string;
  /**
   * P3 (persistent mount): render nothing visible while keeping every hook,
   * store, and in-flight stream alive. Used on CHAT_EXCLUDED_ROUTES so route
   * changes never unmount (and thereby reset) the widget instance.
   */
  hidden?: boolean;
  /** Custom placeholder text for the prompt input */
  placeholder?: string;
  /** Whether to show the dock right button (default: true on desktop) */
  showDockButton?: boolean;
  /** Whether to show the hide button (default: true) */
  showHideButton?: boolean;
  /** Whether to allow file attachments (default: true) */
  allowAttachments?: boolean;
  /** Maximum file size in bytes (default: 50MB — src/constants/uploads.ts) */
  maxFileSize?: number;
  /** Accepted file types for attachments */
  acceptedFileTypes?: string;
  /** Whether to persist chat history to localStorage (default: true) */
  persistHistory?: boolean;
  /** Whether to persist draft messages (default: true) */
  persistDraft?: boolean;
  /** Whether to show the construction waitlist bubble (default: true) */
  showWaitlistBubble?: boolean;
  /** Custom source identifier for analytics/tracking */
  source?: string;
  /** Whether to send messages to platform service when user is logged in */
  sendToPlatform?: boolean;
  /** Optional canned auto-reply, useful when a page-specific bot is not live yet */
  autoReply?: AutoReplyConfig | null;
  /** Custom response handler for when messages are sent */
  onMessageSent?: (message: ChatMessage) => void;
  /** Custom response handler for AI replies */
  onAIResponse?: (response: ChatMessage) => void;
  /** Whether to auto-open chat when user starts typing (default: true) */
  autoOpenOnFocus?: boolean;
  /** Whether to show the chat header with logo (default: true) */
  showHeader?: boolean;
  /** Custom header title (default: "prest0n") */
  headerTitle?: string;
  /** Custom header title as JSX element (takes precedence over headerTitle string) */
  headerTitleElement?: React.ReactNode;
  /** Custom header logo URL */
  headerLogoUrl?: string;
  /** Whether docking is disabled for this instance (useful for embedded chats) */
  disableDocking?: boolean;
  /** Initial docked state */
  initialDocked?: boolean;
  /** When true (default), force chat to start undocked each load to avoid covering page content */
  resetDockOnLoad?: boolean;
  /** When true (default), force chat to start hidden each page load so user sees page content first */
  resetOpenOnLoad?: boolean;
  /** Z-index for the chat container */
  zIndex?: number;
  /** Whether the chat should be visible initially */
  initialOpen?: boolean;
  /** 
   * Whether to use global shared chat history across all pages (default: true).
   * When true, chat history is synced across all GlobalChatSystem instances
   * on different pages (e.g., home page and law firm page share the same history).
   */
  useGlobalHistory?: boolean;
  /** Optional custom handler for user messages; return true to bypass defaults */
  onUserMessage?: OnUserMessageHandler | null;
  /** Whether to show the fullscreen button (default: true on desktop) */
  showFullScreenButton?: boolean;
  /** Optional welcome message shown when chat has no history (fresh session) */
  welcomeMessage?: { text: string; html?: string } | null;
  /**
   * Canonical one-click starter prompts rendered as clickable chips under the
   * empty-thread welcome bubble (F4-A). Bare prompt text (no display quotes);
   * clicking a chip sends the exact string via sendProgrammaticMessage. Empty
   * array → no chip row.
   */
  welcomeStarters?: string[];
  /**
   * Multi-tab greeting parity (2026-08-09): false while the async tenant/
   * account config is still resolving. The greeting composer returns null
   * until this is true (or the field is absent — legacy hosts), so no thread
   * is ever seeded from an unsettled config's default copy. The seeder's
   * documented "composer yields nothing → retry on composer change" contract
   * turns the deferral into a clean retry once the config lands.
   */
  welcomeReady?: boolean;
  /**
   * Where `welcomeMessage` came from (guest/error/generic/firm/tenant/
   * account) — drives the persisted greeting's monotonic rank. See
   * services/threadMetadata/greetingRank.
   */
  welcomeSource?: string;
  /**
   * Memory-distilled NET-NEW automation suggestions for the new-thread
   * greeting (owner directive 2026-07-13): produced server-side from the
   * account's memory (refreshGreetingSuggestions → runtime config docs) and
   * rendered by the greeting composer after filtering against the
   * currently-open thread span. The greeting never references existing
   * threads — these are the only list items it may show.
   */
  suggestedTasks?: string[];
  /**
   * Token usage reported by the server-side suggestion distillation
   * (`accounts/{id}/config/runtime.suggestedAutomationsUsage`, camelCase
   * pass-through). Attached to the greeting bubble so its mandatory ⓘ
   * (owner directive 2026-07-13) shows the real model/token/cost data when
   * AI composed the intro. Null/absent → the ⓘ falls back to its
   * "no extensive compute" copy.
   */
  suggestedTasksUsage?: TokenUsageSnapshot | null;
  /** Optional subtitle shown below the header title (e.g., "Procedures") */
  headerSubtitle?: string;
  /** Optional accent color for the header status dot (default: #10b981) */
  accentColor?: string;
  /** Skip low-balance checks for billing-exempt instances (e.g. citadel). Default: false */
  skipBalanceCheck?: boolean;
  /** Whether to disable balance checking for this chat instance (default: false).
   *  When true, low balance warnings and input blocking are completely suppressed. */
  disableBalanceCheck?: boolean;
  /**
   * LEGACY (2026-08-14, mobile fullscreen takeover): the mode-cycle button
   * is now desktop-only and this flag can no longer bring it back on a
   * narrow viewport — on mobile, open IS fullscreen and hide is the one
   * dismissal. The flag's only remaining effect is on the closed floating
   * pill's docked geometry and the viewport-change undock guard, both
   * meaningful only in the chat-closed-while-docked edge as the viewport
   * crosses 820px. Default: true (DEFAULT_CHAT_CONFIG). Do not set this
   * expecting a mobile mode button.
   */
  forceDockButtonOnMobile?: boolean;
  /**
   * Whether to render the Slack-style thread switcher bar at the bottom of the chat,
   * to the right of the thinking/status bubble area. When enabled users can start
   * multiple parallel threads/projects and switch between them; each thread keeps
   * its own message array in localStorage so conversations never mix.
   *
   * Default: auto — enabled for account-specific Prest0ns, disabled for the
   * internal Prest0n command centre (`source: 'citadel'`) and the prest0-vm
   * infrastructure Prest0n (`source: 'infrastructure'`). Callers can always
   * force-enable or force-disable by setting this explicitly.
   */
  enableThreadSwitcher?: boolean;

  /**
   * Optional Firestore namespace for the user-conversation-history doc.
   * Default omitted → resolves to the canonical `user_history` path
   * (`users/{uid}/user_history/user_history`). Tenant surfaces that
   * use a non-default namespace (e.g. the maestro-chat embed at
   * `widget_maestro_history`) MUST pass it through here so the
   * top-level "clear chat" trash button wipes the right doc.
   *
   * Surgical addition (Miles 2026-05-12, Slack thread
   * C0AM8U804KZ/1778481193.723179): without this pass-through the
   * clear flow always cleared the default-namespace doc, leaving
   * tenant histories alive after the user pressed the trash icon.
   */
  firestoreNamespace?: string;
}

/**
 * Global storage key for shared chat history across all pages.
 * This key is used when useGlobalHistory is true.
 */
export const GLOBAL_CHAT_HISTORY_KEY = 'prest0_global_chat_history';
export const GLOBAL_CHAT_DRAFT_KEY = 'prest0_global_chat_draft';
export const GLOBAL_CHAT_OPEN_KEY = 'prest0_global_chat_open';
export const GLOBAL_CHAT_DOCK_KEY = 'prest0_global_chat_docked';
/**
 * RETIRED (2026-08 scroll redesign): scroll position is no longer persisted —
 * the shared useTranscriptScroll controller keeps per-thread positions in
 * memory only, and a page load always opens at the bottom of the active
 * thread. This ONE global key used to be written by BOTH the production chat
 * and the landing page's (since-deleted) fork, cross-clobbering restores.
 * Kept exported only so stale imports don't break; nothing reads or writes it.
 */
export const GLOBAL_CHAT_SCROLL_KEY = 'prest0_global_chat_scroll';
export const GLOBAL_CHAT_FULLSCREEN_KEY = 'prest0_global_chat_fullscreen';

/**
 * P4 (surface-invariant UI mode, PLAN-chat-surface-consistency-20260712):
 * chat layout state (open/dock/fullscreen — and draft/scroll, which belong to
 * the conversation, not the page) is a USER preference, not a page attribute.
 * One global key per base, identical on every route — the widget keeps its
 * position across navigation, like Slack keeps its layout. Formerly this
 * suffixed keys per-pathname, making the widget "forget" its mode on every
 * page change by design. `pathname` is accepted (and ignored) for caller
 * compatibility; the legacy per-page key builder below exists solely for the
 * one-time migration read.
 */
export function buildPageStorageKey(baseKey: string, pathname?: string): string {
  void pathname;
  return baseKey;
}

/** The pre-P4 per-pathname key shape — used only to SEED the global key once. */
export function buildLegacyPageStorageKey(baseKey: string, pathname?: string): string {
  const pagePath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const safePath = pagePath.replace(/\//g, '_') || '_home';
  return `${baseKey}_page${safePath}`;
}

export function getChatUiStorageKeys(
  instanceId: string,
  useGlobalHistory: boolean = true,
  pathname?: string,
) {
  // Drift fix (P0): this helper previously instance-scoped the bases when
  // useGlobalHistory=false while useGlobalChat always wrote the GLOBAL_*
  // bases — consumers listening via this helper watched keys nobody wrote.
  // It now returns exactly the keys useGlobalChat writes, for every config.
  void instanceId;
  void useGlobalHistory;
  void pathname;
  return {
    CHAT_OPEN: GLOBAL_CHAT_OPEN_KEY,
    CHAT_DOCKED: GLOBAL_CHAT_DOCK_KEY,
    CHAT_FULLSCREEN: GLOBAL_CHAT_FULLSCREEN_KEY,
  };
}

/**
 * Default configuration values
 */
export const DEFAULT_CHAT_CONFIG: Required<GlobalChatConfig> = {
  instanceId: 'global_chat',
  hidden: false,
  placeholder: 'ask about creating, teaching, or monetizing your own ai agent...',
  showDockButton: true,
  showHideButton: true,
  allowAttachments: true,
  maxFileSize: MAX_UPLOAD_BYTES_PER_FILE, // 50MB — see src/constants/uploads.ts
  acceptedFileTypes: ACCEPTED_FILE_TYPES,
  persistHistory: true,
  persistDraft: true,
  showWaitlistBubble: true,
  source: 'global_chat',
  sendToPlatform: true,
  autoReply: null,
  onMessageSent: () => {},
  onAIResponse: () => {},
  autoOpenOnFocus: false,
  showHeader: true,
  headerTitle: 'Prest0n',
  headerTitleElement: null,
  headerLogoUrl: '/images/prest0-logo.webp?v=20251103b',
  disableDocking: false,
  initialDocked: false,
  resetDockOnLoad: true,
  resetOpenOnLoad: true,
  zIndex: 500000,
  initialOpen: false,
  useGlobalHistory: true, // Default to true for shared history across pages
  onUserMessage: null,
  showFullScreenButton: true,
  welcomeMessage: null,
  welcomeStarters: [],
  // Legacy hosts don't gate on config readiness (absent ≠ false — the
  // composer only defers on an explicit false) and rank as 'generic'.
  welcomeReady: true,
  welcomeSource: 'generic',
  suggestedTasks: [],
  suggestedTasksUsage: null,
  headerSubtitle: '',
  accentColor: '#10b981',
  skipBalanceCheck: false,
  disableBalanceCheck: false,
  forceDockButtonOnMobile: true,
  // Thread switcher defaults OFF. It can be opt-in enabled by callers that
  // explicitly want multi-thread/project switching. Account-specific demo and
  // production chat surfaces should preserve the simpler single-thread layout
  // unless the product team intentionally enables this affordance for that
  // surface.
  enableThreadSwitcher: false,
  // Default Firestore namespace for the conversation-history doc.
  // Empty string sentinel → repository falls back to `user_history`.
  firestoreNamespace: '',
};

// ============================================================================
// Thread Switcher Types
// ============================================================================

/**
 * Metadata for a single chat thread/project in the Slack-style switcher.
 * Each thread owns an isolated message array persisted under its own
 * localStorage key so parallel projects never mix.
 */
/** One explicit rename (PR-5): what it was called, what it became, when, by whom. */
export interface ThreadTitleHistoryEntry {
  title: string;
  previousTitle?: string;
  /** Epoch ms. */
  at: number;
  byUid?: string;
  byName?: string;
}

export const THREAD_TITLE_HISTORY_MAX = 50;

export interface ChatThread {
  /** Stable uuid-like identifier for this thread */
  id: string;
  /**
   * MULTI-ORG P4 (2026-08-16): the thread's IMMUTABLE workspace binding
   * ("threads are born into an organization and stay there" — plan D3),
   * stamped at create by the writers and PARSED here for the per-workspace
   * list filter. Absent on pre-binding threads (visible in every workspace
   * until the M4 backfill stamps them). The client NEVER writes this field
   * from the thread object — toChatThreadDoc stamps only from its explicit
   * create-time option, and firestore.rules rejects changes.
   */
  accountId?: string;
  /** Concise title derived from the first user message (or user-set label) */
  title: string;
  /** ISO timestamp when the thread was created */
  createdAt: string;
  /** ISO timestamp of the most recent message activity (recency ordering) */
  lastActiveAt: string;
  /** ISO timestamp of the last time the user had this thread active */
  lastViewedAt: string;
  /** Number of messages the user has not yet viewed in this thread */
  unreadCount: number;
  /**
   * Whether Prest0n is currently processing work in this thread.
   * Persisted so a spinner can be shown on the chip even when the
   * user has switched away to another parallel thread.
   */
  isProcessing: boolean;
  /**
   * Cached message count (for quick summary/badge rendering).
   *
   * SETTLED-ONLY (owner feedback, Miles 2026-08-01): every producer that
   * derives this from a transcript counts via `settledMessageCount`
   * (useChatThreads) — in-flight assistant placeholders (streaming/pending
   * widgets, shared `isInFlightPlaceholderMessage` predicate) are excluded,
   * so the row meta holds at N during a turn and steps to N+1 exactly at
   * finalize. Seeded greetings, user messages, and interrupted cards all
   * count. NOTE: `hasUserContent`'s legacy fallback (`messageCount > 0`) and
   * the empty-delete routing are unaffected — an in-flight assistant turn
   * always follows a settled user/greeting message, so settled-only can
   * never zero a content-bearing thread's count.
   */
  messageCount: number;
  /**
   * Optional 1–2 sentence summary of the thread's current topic, produced
   * by `inferThreadLabel` after each user message. Surfaces in the
   * ThreadSwitcherBar chip hover/focus tooltip. Absent on first render
   * before the inference resolves.
   */
  topicSummary?: string;
  /**
   * True when the user has explicitly renamed this thread via the
   * inline rename UI. Bug G (Miles 2026-05-11): the UI-language
   * backfill pass uses this flag to NEVER overwrite a user-renamed
   * title — only auto-generated titles get re-translated when the UI
   * language and the title language disagree.
   */
  userRenamed?: boolean;
  /**
   * T1 (thread-workflow UX): user-pinned threads render FIRST in every thread
   * list (chip strip + fullscreen sidebar), in pin order. Additive fields —
   * absent on unpinned threads; synced via the thread index doc.
   */
  pinned?: boolean;
  /** ISO timestamp of when the pin was set (pin order = pinnedAt ascending). */
  pinnedAt?: string;
  /** True when archived on server; omitted for active records. */
  archived?: boolean;
  /**
   * USER-CONTENT SEMANTICS (personalized greeting, 2026-07-13): true when the
   * thread's transcript contains at least one USER message. Threads seeded
   * with an assistant greeting have `messageCount > 0` while still being
   * EMPTY DRAFTS for every lifecycle decision — single-empty reuse
   * (`findReusableEmptyThread`), deferred server create
   * (`shouldDeferServerCreate`), and one-click empty delete. Additive field:
   * absent on legacy records, where consumers fall back to `messageCount > 0`
   * (see `threadRecordHasUserContent`). Synced via the thread index doc.
   */
  hasUserContent?: boolean;
  /**
   * SERVER-OWNED shared-thread marker (live-membership surfacing, 2026-08-16):
   * true when the membership backend has registered this thread as shared
   * with other participants. Read-only on the client (toChatThreadDoc omits
   * it): gates the group-message overlay listener, the sidebar's
   * "group threads" section, and the membership-refresh trigger — so a thread
   * the server just pushed into this user's index behaves as a group thread
   * immediately, before any membership re-read.
   */
  sharedThread?: boolean;
  /** Uid behind the latest server activity bump ('' ⇒ assistant/unknown). */
  lastActivityBy?: string;
  /**
   * PERSONAL ORGANIZATION (chat-thread experience PR-5, 2026-09-09): the id of
   * the viewer's category this thread is filed under — one of
   * `users/{uid}/chat_threads_meta/categories.items`. Client-owned on the
   * viewer's OWN index doc; nobody else sees it. Absent ⇒ the thread sits in
   * its built-in section (direct messages / group threads / threads).
   */
  category?: string;
  /**
   * NAME HISTORY (PR-5): every explicit rename of this thread, oldest first,
   * capped at 50 — searchable, so a thread found by a former name still
   * opens. Solo threads record it here (the viewer's own index doc); shared
   * threads carry the unified history on the membership registry, which the
   * membership read projects (`MembershipReadThread.titleHistory`).
   */
  titleHistory?: ThreadTitleHistoryEntry[];
  /**
   * The VIEWER's per-thread notification bell (chime / chime-platform / off),
   * mirrored onto the index doc so the server push fan-out can honor a mute
   * (per-thread notification badges, 2026-09-08). Client-written (this is the
   * viewer's own index doc); absent until the bell is first cycled — the
   * Settings defaults decide meanwhile, on both sides.
   */
  notifyMode?: 'chime' | 'chime-platform' | 'off';
  /**
   * ROUTINE MIRROR (Routine Threads 2026-07-31; v2 2026-08-16) — SERVER-OWNED,
   * read-only on the client. Presence of `routineId` marks this thread as a
   * ROUTINE THREAD: it holds ONE OR MORE routines (see `routineCount`),
   * surfaces in the sidebar's "Routines" section, wears the RoutineGlyph, and
   * every run posts a visible assistant message here. With several routines
   * the singular fields describe the PRIMARY one (soonest next run). Written
   * by the routines backend via Admin SDK onto the index doc; parsed by
   * fromChatThreadDoc and DELIBERATELY OMITTED from toChatThreadDoc so client
   * upserts can never clobber them (removing the last routine deletes them
   * all — the thread reverts to a normal thread, history intact).
   */
  routineId?: string;
  /** Short human routine name, e.g. "Morning market summary". */
  routineName?: string;
  /** 'active' when ANY routine here is active; 'paused' when all are dormant. */
  routineStatus?: 'active' | 'paused';
  /** Human-readable cadence — the primary routine's schedule summary, or its
   * trigger summary for event-trigger routines ("Every time a new email arrives"). */
  routineScheduleSummary?: string;
  /** ISO of the soonest scheduled run across the thread's active routines;
   * null while paused/unscheduled (and for trigger-only threads — no clock). */
  routineNextRunAt?: string | null;
  /** ISO of the most recent completed run. */
  routineLastRunAt?: string;
  /** Outcome of the most recent run (manager panel status dot). */
  routineLastRunStatus?: 'ok' | 'error';
  /** Routines v2: how many routines this thread holds (absent on pre-v2
   * mirrors — treat absence as 1). */
  routineCount?: number;
  /**
   * HANDOFF (auto-created routine thread): the origin thread the user asked
   * in. When this matches the viewer's ACTIVE thread, no ack is stamped, and
   * `routineHandoffAt` is fresh (≤5 min), the client auto-navigates here once
   * and persists `routineHandoffAckAt` — see useChatThreads' handoff effect.
   */
  routineHandoffFromThreadId?: string;
  /** ISO stamp of when the server minted the handoff thread. */
  routineHandoffAt?: string;
  /** ISO written by the CLIENT after it auto-navigated (idempotence guard). */
  routineHandoffAckAt?: string;
}

/**
 * Source identifiers (from `GlobalChatConfig.source`) that represent
 * internal Prest0n instances (command centre and prest0-vm) which MUST
 * NOT show the thread switcher regardless of `enableThreadSwitcher`.
 */
export const INTERNAL_PREST0N_SOURCES: ReadonlySet<string> = new Set([
  'citadel',        // internal Prest0n command centre
  'infrastructure', // prest0-vm / compute management
]);

/**
 * instanceId values that represent internal Prest0n instances.
 * Kept separate from source because future internal tools may share
 * a custom instanceId without matching on source.
 */
export const INTERNAL_PREST0N_INSTANCE_IDS: ReadonlySet<string> = new Set([
  'citadel_chat',
  'prest0_vm_chat',
  'prest0vm_chat',
]);

export function getThreadStorageKeyPrefixes(instanceId: string) {
  const safe = instanceId || 'global_chat';
  return {
    THREAD_LIST: `prest0_threads__${safe}`,
    ACTIVE_THREAD: `prest0_active_thread__${safe}`,
    /**
     * Prefix for per-thread message arrays. Full key is
     * `prest0_thread_msgs__<instanceId>__<threadId>`.
     */
    THREAD_MSG_PREFIX: `prest0_thread_msgs__${safe}__`,
    /**
     * Archive of threads the user has "deleted" from the visible bar.
     * Messages are preserved under this key so nothing is lost from
     * general memory — future UI can restore archived threads.
     * Shape: ArchivedThread[] (newest-first).
     */
    THREAD_ARCHIVE: `prest0_thread_archive__${safe}`,
  };
}

/** localStorage key helpers for the thread switcher (scoped per instance + optional uid). */
export function getThreadStorageKeys(instanceId: string, uid?: string | null) {
  const prefixes = getThreadStorageKeyPrefixes(instanceId);
  const uidSafe = typeof uid === 'string' ? uid.trim() : '';
  if (!uidSafe) {
    return prefixes;
  }
  const uidSegment = `__u_${uidSafe}`;
  const threadMsgBase = prefixes.THREAD_MSG_PREFIX.endsWith('__')
    ? prefixes.THREAD_MSG_PREFIX.slice(0, -2)
    : prefixes.THREAD_MSG_PREFIX;
  return {
    THREAD_LIST: `${prefixes.THREAD_LIST}${uidSegment}`,
    ACTIVE_THREAD: `${prefixes.ACTIVE_THREAD}${uidSegment}`,
    THREAD_MSG_PREFIX: `${threadMsgBase}${uidSegment}__`,
    THREAD_ARCHIVE: `${prefixes.THREAD_ARCHIVE}${uidSegment}`,
  };
}

/**
 * G3 guest→member adoption once-marker (per instance + uid). Present while a
 * sign-in era's SERVER adoption has already run for this uid on this device,
 * so a double hydrate / second tab cannot re-push an adopted transcript over
 * newer server state. Cleared by `purgeThreadStorageForSessionReset` at
 * sign-out — a NEW guest era's threads must adopt again on the next sign-in.
 */
export function getGuestAdoptionMarkerKey(instanceId: string, uid: string): string {
  const safe = instanceId || 'global_chat';
  return `prest0_thread_guest_adopted__${safe}__u_${uid}`;
}

/**
 * G3 guest-era stamp (per instance, deliberately UNSCOPED — it describes the
 * unscoped store itself). Written idempotently by the first unscoped
 * per-thread persist of a GUEST session (uid null; see
 * `safelyPersistThreadMessages` in threadPersist.ts) and cleared by
 * `purgeThreadStorageForSessionReset`, so a new guest era re-stamps on its
 * first write.
 *
 * Why it exists (pre-G3 residue guard, 2026-07-15 review finding 1): the
 * legacy-owner marker is only written when a MIGRATION run finds legacy data,
 * so a pre-G3 build that leaked a member's pane into the unscoped store at
 * sign-out left no attribution at all — "no marker + no scoped keys" is not
 * proof of guest ownership. `adoptGuestThreadsToServer` therefore requires
 * THIS stamp before pushing migrated unscoped content into a signed-in uid's
 * server store: un-stamped residue still migrates locally (unchanged
 * behavior) but is never durably attributed to the next account.
 */
export function getGuestEraMarkerKey(instanceId: string): string {
  const safe = instanceId || 'global_chat';
  return `prest0_thread_guest_era__${safe}`;
}

/**
 * An archived thread record. Written when the user removes a thread from
 * the switcher bar; keeps the original metadata + full message history so
 * the conversation is preserved in general memory and can be restored.
 */
export interface ArchivedThread {
  thread: ChatThread;
  messages: ChatMessage[];
  archivedAt: string; /* ISO timestamp */
}

// ============================================================================
// State Types
// ============================================================================

/**
 * The complete state of the global chat system.
 */
/**
 * Unified state machine for the chat input and indicator UI.
 */
export interface ChatInputState {
  status: 'idle' | 'computing' | 'awaiting_user_input' | 'error';
  variant?: string;
  payload?: any;
}

export interface GlobalChatState {  inputState: ChatInputState;
  /** Whether the chat panel is currently open/visible */
  isChatOpen: boolean;
  /** Whether the chat is docked to the right side of the screen */
  isChatDockedRight: boolean;
  /** Whether the chat is in fullscreen mode */
  isChatFullScreen: boolean;
  /** All messages in the current conversation */
  messages: ChatMessage[];
  /** Current prompt text (plain text) */
  prompt: string;
  /** Current prompt HTML (rich text) */
  promptHtml: string;
  /** Files attached to the current message */
  attachments: File[];
  /** Whether the attachment preview panel is visible */
  isAttachmentPanelVisible: boolean;
  /** Whether the user has manually collapsed the attachment panel */
  userCollapsedAttachments: boolean;
  /** Whether the attachment panel was visible when chat was hidden */
  lastAttachmentVisibleOnHide: boolean;
  /** Whether files are being dragged over the prompt area */
  isDragging: boolean;
  /** Whether we're on a mobile viewport */
  isMobileViewport: boolean;
  /** Whether a processing operation is in progress (prevents new message sends) */
  isProcessing: boolean;
  /** Timestamp when processing started (for safety timeout) */
  processingStartedAt: number | null;
  /**
   * Per-thread processing state. Map from threadId → true when that
   * thread has an in-flight request. This is the SOURCE OF TRUTH for
   * the per-chip spinner; the global `isProcessing` flag is now derived
   * ("any thread is processing") and continues to drive the composer's
   * send-button gate. Without this map, sending a message on thread A
   * would render the spinner on thread B's chip and would let thread
   * B's stopProcessing() clear thread A's spinner.
   *
   * NOTE: serialized along with the rest of GlobalChatState — but the
   * map is intentionally NOT persisted to localStorage (transient
   * in-flight state only). See thread-isolation HARD RULE in PR
   * fix/thread-isolation-per-thread-state.
   */
  processingByThread: Record<string, boolean>;
  /** Per-thread processingStartedAt for the safety-timeout floor. */
  processingStartedAtByThread: Record<string, number>;
  /**
   * Per-thread turn-start epoch for the elapsed ("working · 0:07") clock.
   * Stamped once on the rising edge and stable for the whole turn — unlike
   * `processingStartedAtByThread`, which is re-stamped mid-turn to keep the
   * safety-timeout floor fresh and therefore reset the clock. Transient
   * in-flight state (not persisted); optional for back-compat with older
   * serialized snapshots.
   */
  turnStartedAtByThread?: Record<string, number>;
  /**
   * Per-thread pending-message queue.
   *
   * When the user sends a SECOND message on the SAME thread while
   * the first turn is still streaming, the second user bubble is
   * appended to `messages` immediately (visible in history) and a
   * mirror PendingMessage is pushed onto this thread's queue. The
   * orchestrator dequeues the head once the in-flight turn finishes
   * and fires it as a fresh turn (with the FULL conversation history
   * including the new user bubble).
   *
   * Map<threadId → ordered PendingMessage[]>. Index 0 is the head
   * (next to fire). FIFO.
   *
   * See `threadQueue.ts` for the pure helpers and the unit tests.
   * NOT persisted to localStorage — pending entries are transient and
   * tied to in-flight orchestrator state.
   *
   * Miles directive (2026-05-09 23:44 UTC): "yes it should be allowed".
   */
  pendingByThread: Record<string, PendingMessage[]>;
  /** Whether the clear chat confirmation modal is visible */
  showClearConfirmModal: boolean;
}

/**
 * Actions that can be dispatched to update the chat state.
 */
export type GlobalChatAction =
  | { type: 'SET_CHAT_OPEN'; payload: boolean }
  | { type: 'SET_CHAT_OPEN_PRIORITY'; payload: boolean }
  | { type: 'SET_CHAT_DOCKED_RIGHT'; payload: boolean }
  | { type: 'SET_CHAT_FULLSCREEN'; payload: boolean }
  | { type: 'SET_MESSAGES'; payload: ChatMessage[] }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'ADD_MESSAGES'; payload: ChatMessage[] }
  | { type: 'UPDATE_MESSAGE'; payload: { index: number; message: Partial<ChatMessage> } }
  | {
      type: 'UPDATE_MESSAGE_BY_ID';
      payload: {
        id: string;
        message: Partial<ChatMessage>;
        /**
         * INCIDENT-20260818 ("came back without the spinning widget"): when
         * the id is not in state (state lagging thread storage on the ACTIVE
         * thread — hydrate/resume timing), append the message instead of
         * silently no-opping, so a resumed turn's pending widget and final
         * answer are always visible. Callers set this ONLY after verifying
         * the target thread is the active one — appending a background
         * thread's message into the active slot would be cross-thread
         * materialization, the exact bug the ownership guard closed.
         */
        appendIfMissing?: boolean;
      };
    }
  | { type: 'REMOVE_MESSAGE'; payload: { index: number } }
  | { type: 'REMOVE_MESSAGE_BY_ID'; payload: { id: string } }
  | { type: 'SET_PROMPT'; payload: string }
  | { type: 'SET_PROMPT_HTML'; payload: string }
  | { type: 'SET_ATTACHMENTS'; payload: File[] }
  | { type: 'ADD_ATTACHMENTS'; payload: File[] }
  | { type: 'REMOVE_ATTACHMENT'; payload: number }
  | { type: 'CLEAR_ATTACHMENTS' }
  | { type: 'SET_ATTACHMENT_PANEL_VISIBLE'; payload: boolean }
  | { type: 'SET_USER_COLLAPSED_ATTACHMENTS'; payload: boolean }
  | { type: 'SET_LAST_ATTACHMENT_VISIBLE_ON_HIDE'; payload: boolean }
  | { type: 'SET_DRAGGING'; payload: boolean }
  | { type: 'SET_MOBILE_VIEWPORT'; payload: boolean }
  | { type: 'RESET_PROMPT' }
  | { type: 'CLEAR_CHAT' }
  | {
      type: 'SET_PROCESSING';
      payload: {
        isProcessing: boolean;
        timestamp?: number;
        threadId?: string | null;
        /** Rising-edge clock override: when the server tells us a task has been
         * running since `turnEpochMs`, the row chip shows its true age instead
         * of restarting at 0:00. Ignored on a thread already processing. */
        turnEpochMs?: number;
      };
    }
  | { type: 'ENQUEUE_PENDING'; payload: PendingMessage }
  | { type: 'DEQUEUE_PENDING_HEAD'; payload: { threadId: string } }
  | { type: 'FLUSH_PENDING'; payload: { threadId: string } }
  | { type: 'REMOVE_PENDING_FOR_THREAD'; payload: { threadId: string } }
  | { type: 'SET_INPUT_STATE'; payload: ChatInputState }
  | { type: 'SET_CLEAR_CONFIRM_MODAL'; payload: boolean };

// ============================================================================
// Context Types
// ============================================================================

/**
 * The value provided by the GlobalChatContext.
 */
export interface GlobalChatContextValue {
  /** Current state of the chat system */
  state: GlobalChatState;
  /** Configuration for the chat system */
  config: Required<GlobalChatConfig>;
  /** Dispatch function to update state */
  dispatch: React.Dispatch<GlobalChatAction>;
  /** Reference to the prompt input element */
  promptInputRef: React.RefObject<HTMLDivElement>;
  /** Reference to the chat messages container */
  chatMessagesRef: React.RefObject<HTMLDivElement>;
  /** Reference to the file input element */
  fileInputRef: React.RefObject<HTMLInputElement>;
  /** Reference to the prompt bar container */
  promptBarRef: React.RefObject<HTMLDivElement>;
  /** Reference to the floating prompt bar */
  floatingPromptBarRef: React.RefObject<HTMLDivElement>;
  /** Reference to the chat container */
  chatContainerRef: React.RefObject<HTMLDivElement>;
  /** Construction waitlist controller */
  waitlistController: ConstructionWaitlistController;
  /** Handler functions */
  handlers: GlobalChatHandlers;
}

/**
 * Handler functions for chat interactions.
 */
export interface GlobalChatHandlers {
  /** Send the current message */
  handleSend: () => Promise<void>;
  /** Handle input changes in the prompt */
  handleInput: (e: React.FormEvent<HTMLDivElement>) => void;
  /** Handle paste events in the prompt */
  handlePaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  /** Handle key down events in the prompt */
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, source?: 'main' | 'floating') => void;
  /** Toggle dock state */
  handleToggleDock: () => void;
  /** Toggle fullscreen mode */
  handleToggleFullScreen: () => void;
  /** Cycle through layout modes: default → docked → fullscreen → default */
  handleCycleMode: () => void;
  /** Open the file selector */
  handleFileSelect: () => void;
  /** Handle file input change */
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Remove an attachment by index */
  handleRemoveAttachment: (index: number) => void;
  /** Clear all attachments */
  handleClearAttachments: () => void;
  /** Toggle attachment panel visibility */
  toggleAttachmentPanel: () => void;
  /** Handle attachment button click */
  handleAttachmentButtonClick: (e?: React.MouseEvent) => void;
  /** Handle drag enter */
  handleDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Handle drag leave */
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Handle drag over */
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Handle file drop */
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Hide the chat panel */
  handleHide: () => void;
  /** Open the chat panel */
  handleOpen: () => void;
  /** Get current plain text from prompt */
  getCurrentPlainText: () => string;
  /** Handle prompt input focus - prevents sync from overwriting while typing */
  handlePromptFocus: () => void;
  /** Handle prompt input blur - allows sync to resume */
  handlePromptBlur: () => void;
  /** Show clear chat confirmation modal */
  handleClearChat: () => void;
  /** Actually clear chat (called from modal confirm) */
  confirmClearChat: () => Promise<void>;
  /** Hide clear chat confirmation modal */
  hideClearConfirmation: () => void;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for the main GlobalChatSystem component.
 */
export interface GlobalChatSystemProps {
  /** Configuration options */
  config?: Partial<GlobalChatConfig>;
  /** Optional className for styling */
  className?: string;
  /** Optional children to render inside the chat (e.g., custom messages) */
  children?: React.ReactNode;
}

/**
 * Props for the GlobalChatProvider component.
 */
export interface GlobalChatProviderProps {
  /** Configuration options */
  config?: Partial<GlobalChatConfig>;
  /** Children components that will have access to the chat context */
  children: React.ReactNode;
}

/**
 * Props for the ChatContainer component.
 */
export interface ChatContainerProps {
  /** Whether the chat is currently open */
  $isOpen: boolean;
  /** Whether the chat is docked to the right */
  $isDocked: boolean;
}

/**
 * Props for the Message component.
 */
export interface MessageProps {
  /** Whether this is a user message */
  isUser: boolean;
}

/**
 * Props for file-related components.
 */
export interface FilePreviewProps {
  /** The file to preview */
  file: File;
  /** Callback when remove is clicked */
  onRemove: () => void;
}

export interface ClickableAttachmentProps {
  /** The file attachment */
  file: File;
  /** Whether this is in a user message bubble */
  isUser: boolean;
}

export interface AttachmentIconProps {
  /** The file MIME type */
  fileType: string | undefined;
  /** Whether this is in a user message bubble */
  isUser: boolean;
}

// ============================================================================
// Special Message Tile Types (Reusable Message Cards with Action Buttons)
// ============================================================================

/**
 * Message type variants for special message tiles.
 * Used for styling (border color, icon) and semantic categorization.
 */
export type SpecialMessageType =
  | 'notification'
  | 'alert'
  | 'info'
  | 'warning'
  | 'error'
  | 'success'
  | 'system'
  | 'research'    // Deep research workflow
  | 'formatting'  // Formatting workflow
  | 'progress'    // In-progress/streaming states
  | 'payment'     // Payment gate workflow - inline invoice form
  | 'appointment'; // Appointment booking widget - post-payment scheduling

/**
 * Priority level for special message tiles.
 * Affects visual prominence and behavior.
 */
export type SpecialMessagePriority = 'urgent' | 'high' | 'normal' | 'low';

/**
 * Button style variants for action buttons in special message tiles.
 */
export type ActionButtonStyle = 'primary' | 'secondary' | 'link' | 'destructive';

/**
 * Action button configuration for special message tiles.
 * These buttons can trigger navigation, URL opens, callbacks, and more.
 */
export interface SpecialMessageActionButton {
  /** User-visible label for the button */
  buttonText: string;
  /** Type of action to execute when clicked */
  actionType:
    | 'navigate'      // Client-side navigation within app
    | 'open_url'      // Open external URL in new tab
    | 'deep_link'     // Navigate to app deep link
    | 'dismiss'       // Dismiss/close the message
    | 'copy_text'     // Copy text to clipboard
    | 'callback'      // Execute a callback function
    | 'download'      // Download a file/attachment
    | 'form'          // Show inline form input
    | 'confirm'       // Show confirmation dialog
    | 'custom';       // Custom action handler
  /** Value associated with the action (URL, route, text, etc.) */
  actionValue: string;
  /** Visual style of the button */
  buttonStyle: ActionButtonStyle;
  /** Optional callback function for 'callback' action type */
  onClick?: () => void | Promise<void>;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Optional loading state */
  loading?: boolean;
}

/**
 * Props for the SpecialMessageTile component.
 * A reusable message card with content, optional icon, and action buttons.
 */
export interface SpecialMessageTileProps {
  /** Message content - plain text */
  text?: string;
  /** Message content - HTML (takes precedence over text if provided) */
  html?: string;
  /** Owning message id — forwarded to attachment previews for the resolver's
   *  locator-free context lookup (2026-08-09 preview fix). */
  messageId?: string;
  /** Type of message for styling and semantics */
  messageType?: SpecialMessageType;
  /** Priority level for visual prominence */
  priority?: SpecialMessagePriority;
  /** Optional title displayed above the content */
  title?: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional custom icon element to override default type icon */
  icon?: React.ReactNode;
  /** Whether to show the default type icon (default: true) */
  showIcon?: boolean;
  /** Action buttons to display */
  actionButtons?: SpecialMessageActionButton[];
  /** Timestamp for the message */
  timestamp?: Date;
  /** Whether to show the timestamp (default: false) */
  showTimestamp?: boolean;
  /** Optional expiration time (message can auto-dismiss) */
  expiresAt?: Date;
  /** Callback when message is dismissed (via button or expiration) */
  onDismiss?: () => void;
  /** Callback when any action is triggered (for analytics/logging) */
  onAction?: (action: SpecialMessageActionButton) => void;
  /** Whether the tile is in a loading/streaming state */
  isStreaming?: boolean;
  /** Optional className for custom styling */
  className?: string;
  /** Whether to animate on mount (default: true) */
  animate?: boolean;
  /** Optional attachments to display below the content */
  attachments?: File[];
  /** Optional serialized attachments */
  attachmentsSerialized?: SerializedAttachment[];
  /** Token usage metadata from AI model responses - displays info popup when present */
  tokenUsage?: TokenUsageMetadata;
  /** Persisted token usage snapshot for durability across sessions */
  tokenUsageSnapshot?: TokenUsageSnapshot;
  /** Multiple token usages for multi-model responses */
  tokenUsages?: TokenUsageMetadata[];
  /** Persisted snapshots for multi-model responses */
  tokenUsageSnapshots?: TokenUsageSnapshot[];
  /** HTML for workflow action buttons - rendered after attachments */
  workflowActionButtonsHtml?: string;
  /** Source tag for the message (used for identification) */
  sourceTag?: string;
  /** Callback to copy message content to clipboard */
  onCopyMessage?: () => void;
  /**
   * Read aloud (2026-09-09): the host hands the tile the same control the normal
   * bubble's meta row carries, already wired to the app-wide readout store, so a
   * card is not the one message shape you cannot listen to. Absent = no control
   * (the lever is off, or the host has no id for this message).
   */
  readAloud?: {
    state: 'idle' | 'loading' | 'playing' | 'paused';
    label: string;
    isActive: boolean;
    onToggle: () => void;
    /** The player's own numbers, so an active tile can carry the transport too. */
    progress: number;
    elapsedSeconds: number;
    preparingNext?: boolean;
    rate: number;
    language?: string;
  };
  /** Tags for categorization (e.g., "test example" for test data) */
  tags?: string[];
  /** Index of selected action button (for persisting user's button choice) */
  selectedActionButtonIndex?: number;
  /**
   * Whether this tile's workflow CTA buttons may still be pressed
   * (workflowCtaLifecycle.isWorkflowCtaLive — newest visible message in an
   * idle thread). When false, workflow CTA buttons render greyed out and
   * unclickable; utility buttons (copy/stop/preview) are untouched.
   * Defaults to true so call sites that never carry workflow CTAs are
   * unaffected.
   */
  ctaLive?: boolean;
  /**
   * ONE ⓘ PER TURN (owner directive 2026-09-01): when true, the tile footer
   * omits compute time + the ⓘ model-info button because the turn's answer
   * bubble owns the attribution (turnAttribution.cardAttributionOwnedByTurnAnswer).
   * Defaults to false — a card-only turn keeps its ⓘ.
   */
  suppressAttribution?: boolean;
}

/**
 * Extended ChatMessage type with action button support.
 * Used when messages need interactive elements.
 */
export interface ChatMessageWithActions extends ChatMessage {
  /** Action buttons for this message */
  actionButtons?: SpecialMessageActionButton[];
  /** Message type for special styling */
  messageType?: SpecialMessageType;
  /** Priority level */
  priority?: SpecialMessagePriority;
  /** Optional title for the message */
  title?: string;
}

// ============================================================================
// Unified Chat Attachment Types
// ============================================================================

/**
 * Attachment variant types for different contexts.
 * Used for contextual labeling (e.g., "research report", "formatted report").
 * 
 * Note: All variants now use the same unified blue color scheme (#2196f3)
 * for visual consistency across the application.
 */
export type AttachmentVariant = 'default' | 'research' | 'formatted';

/**
 * Props for the unified ChatAttachment component.
 * Handles both File objects and SerializedAttachment with a consistent
 * blue color scheme across all attachment types.
 */
export interface ChatAttachmentProps {
  /** Hydrated File object (from user upload or generated) */
  file?: File;
  /** Serialized attachment data (for persistence/restoration) */
  serialized?: SerializedAttachment;
  /** Variant for contextual labeling (all use unified blue color) */
  variant?: AttachmentVariant;
  /** Whether to animate the attachment on mount (default: true) */
  animate?: boolean;
  /** Owning message id — lets the preview resolver reconstruct the storage
   *  path when the record carries no locator (2026-08-09 preview fix). */
  messageId?: string;
  /** Owning THREAD id (2026-08-24): a message record carries no thread of its
   *  own — membership is positional — but the transcript on screen IS the
   *  active thread, so the render site knows it for free. Rides with the
   *  preview so "open chat" can return to this conversation later. */
  threadId?: string;
  /** That thread's title, for switchThread's minimal-record path when the
   *  conversation is no longer in the local list (archived, other device). */
  threadTitle?: string;
  /** True when the tile renders inside the GOLD user bubble — switches the
   *  card to the white + warm-ink variant (2026-08-20 contrast fix). */
  onUserBubble?: boolean;
}

/**
 * Props for the AttachmentsSection wrapper component.
 */
export interface AttachmentsSectionProps {
  /** Source tag from the message for determining variant */
  sourceTag?: string;
  /** Hydrated File attachments */
  attachments?: File[];
  /** Serialized attachments for fallback */
  attachmentsSerialized?: SerializedAttachment[];
  /** Whether this is in a user message bubble (for styling) */
  isUser?: boolean;
}

// ============================================================================
// Storage Keys
// ============================================================================

/**
 * Generate storage keys for a specific chat instance.
 * @param instanceId - The unique identifier for this chat instance
 * @param useGlobalHistory - Whether to use the global shared history key
 */
export function getChatStorageKeys(instanceId: string, useGlobalHistory: boolean = true) {
  return {
    // Use global keys when sharing history/draft/dock across pages
    CHAT_HISTORY: useGlobalHistory ? GLOBAL_CHAT_HISTORY_KEY : `${instanceId}_chat_history`,
    CHAT_DRAFT: useGlobalHistory ? GLOBAL_CHAT_DRAFT_KEY : `${instanceId}_chat_draft`,
    CHAT_DOCKED: useGlobalHistory ? GLOBAL_CHAT_DOCK_KEY : `${instanceId}_chat_docked`,
    CHAT_SCROLL: useGlobalHistory ? GLOBAL_CHAT_SCROLL_KEY : `${instanceId}_chat_scroll`,
  };
}

// ============================================================================
// Layout Constants
// ============================================================================

export const CHAT_LAYOUT = {
  // THE side convention (owner directive 2026-08-20): every open path —
  // current AND future — anchors the chat overlay on the RIGHT. Consumers
  // must not introduce a left branch; pinned by dockedRightConvention test.
  DOCK_SIDE: 'right',
  BOTTOM_OFFSET: '1.5rem',
  WIDTH: 'min(900px, 92vw)',
  // Docked pane width. The calc() guard keeps the right-pinned pane
  // (`right: 24px`) from ever exceeding the viewport in the base >900px
  // rule — a quirky viewport (browser zoom, scrollbar-inclusive widths)
  // must shrink the pane, never clip it. Both consumers (ChatContainer's
  // docked branch and DockedCollapsedBar) interpolate this as a raw CSS
  // width string, so the min() propagates to both automatically.
  DOCKED_WIDTH: 'min(504px, calc(100vw - 48px))',
  // The same pane width as a NUMBER, for surfaces that compute geometry in
  // JS instead of CSS — floating preview windows pick an opening rect that
  // clears the docked rail (dockedCompanion.ts). MUST stay in step with the
  // string above; dockedCompanion.test.ts pins that the two agree.
  DOCKED_WIDTH_PX: 504,
  DOCKED_TOP: 96, // px offset to sit below top nav/login
  DOCKED_BOTTOM: 24, // px breathing room at bottom when docked
  // Clear air between the docked rail and whatever shares the screen with
  // it (page content in App's RoutedContentShell, the companion preview
  // panel). Same value as the rail's own 24px outer inset, so the pane sits
  // in a visually even three-column rhythm: 24 | content | 24 | pane | 24.
  DOCKED_GUTTER: 24,
  PROMPT_BAR_GAP: '1.5rem',
} as const;
